import { FunctionComponent, useEffect, useRef, useState } from "react";
import Table from "../../../../Components/Table/Table";
import CustomToolbar from "../../../../Components/Table/components/CustomToolbar";
import ButtonField from "../../../../Components/Button/ButtonField";
import { Columns } from "../../../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import TableSearch from "../../../../Components/Table/components/TableSearch";
import IconButtonField from "../../../../Components/Button/IconButtonField";
import {
  SvgIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { ReactComponent as Edit } from "../../../../Assets/Images/edit.svg";
import { ReactComponent as RefreshButton } from "../../../../Assets/Images/RefreshButton.svg";

import ResponseDialog from "../../../../Components/ResponseDialog/ResponseDialog";
import { IClassSelectionMapping } from "./IClassSelection";
import ClassSelectionForm from "./ClassSelectionForm";
import { ClassSelectionMasterRead } from "../../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterRead";
import {
  ClassSelectionCreate_Update,
  clearClassSelectionCreate_Update,
} from "../../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterCreate_Update";
import DeleteDialog from "../../../../Components/DeleteDialog/DeleteDialog";
import { IDeleteDialog } from "../../../../Components/DeleteDialog/IDeleteDialog";
import { ReactComponent as Delete } from "../../../../Assets/Images/delete.svg";

const ClassSelectionMaster: FunctionComponent = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] =
    useState<IClassSelectionMapping | null>(null);



  const [initialValues, setInitialValues] = useState<
    Partial<IClassSelectionMapping>
  >({
    status: true,
  });

  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useAppDispatch();

  const { data: classSelectionList } = useAppSelector(
    (state) => state.ClassSelectionMasterRead
  );
  const { data: createOrUpdateResponse } = useAppSelector(
    (state) => state.ClassSelectionCreate_Update
  );

const formatDate = (rawDate: string) => {
  if (!rawDate) return "-";

  const [year, month, day] = rawDate.split("-");

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const monthName = monthNames[parseInt(month, 10) - 1];

  return `${day}-${monthName}-${year}`;
};

  // Columns
  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 80,
      headerAlign: "center",
      align: "center",
    },
 {
  field: "date",
  headerName: "Date",
  flex: 1,
  align: "center",
  headerAlign: "center",
  renderCell: (params) => {

    // FIX: flatten nested dates
    const allDates = params.row?.dates?.flatMap((x: any) => x.dates) || [];

    if (allDates.length === 0) return "-";

    const firstTwo = allDates
      .slice(0, 2)
      .map((d: any) => formatDate(d.date));

    const moreCount = allDates.length - 2;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{firstTwo.join(" | ")}</span>

        {moreCount > 0 && (
          <span style={{ fontWeight: "bold" }}>+{moreCount} More</span>
        )}
      </div>
    );
  },
},   {
      field: "tutor_name",
      renderHeader: () => <b>Tutor</b>,
      flex: 1,
      type: "string",
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.tutor?.name || "-",
    },
  {
  field: "subject_name",
  renderHeader: () => <b>Subject</b>,
  flex: 1,
  align: "center",
  headerAlign: "center",
  renderCell: (params) => {
    const allSubjectNames = params.row?.dates?.flatMap((d: any) => d.subjects.map((s: any) => s.name)) || [];

    if (allSubjectNames.length === 0) return "-";

    const uniqueNames = allSubjectNames.filter(
      (name:string, index:number) => allSubjectNames.indexOf(name) === index
    );

    const firstTwo = uniqueNames.slice(0, 2).join(" | ");
    const more = uniqueNames.length - 2;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{firstTwo}</span>
        {more > 0 && (
          <span style={{ fontWeight: "bold" }}>+{more} More</span>
        )}
      </div>
    );
  },
},
    {
      field: "center_name",
      renderHeader: () => <b>Center</b>,
      flex: 1,
      type: "string",
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.center?.center_name || "-",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      flex: 1,
      align: "center",
      headerAlign: "center",
      cellClassName: (params) =>
        `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) => (params.value ? "Active" : "Inactive"),
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<IClassSelectionMapping>) => (
        <>
          <IconButtonField
            onClick={() => {
              const row = params.row;

              setInitialValues({
                ...row,
                tutor_id: row.tutor_id || row.tutor?.id || 0,
                center_id: row.center_id || row.center?.center_id || "",
                dates: row.dates || [],
              });

              setDialogOpen(true);
              setIsEdit(true);
            }}
            disabled={dialogOpen}
          >
            <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
          </IconButtonField>
          <IconButtonField
            onClick={() => {
              dispatch(clearClassSelectionCreate_Update());
              setDeleteData({ ...params.row });
              setDeleteDialogOpen(true);
            }}
          >
            <SvgIcon component={Delete} inheritViewBox />
          </IconButtonField>
        </>
      ),
    },
  ];

  // Add index to rows
  const classSelections = Array.isArray(classSelectionList?.data)
    ? classSelectionList?.data.map((item, index) => ({
        ...item,
        index: index + 1,
      }))
    : [];

  useEffect(() => {
    dispatch(ClassSelectionMasterRead());
  }, [dispatch, createOrUpdateResponse]);

  useEffect(() => {
    return () => {
      dispatch(clearClassSelectionCreate_Update());
    };
  }, [dispatch]);

  return (
    <>
      <Table
        rows={classSelections ?? []}
        columns={columns}
        getRowId={(row) => row.class_selection_master_id}
      >
        <CustomToolbar>
          <h6>Class Selection</h6>
          <TableSearch />
          <ButtonField
            className="tabel_add_new_button"
            onClick={() => {
              setInitialValues({
                class_selection_master_id: "0",
                status: true,
                tutor_id: 0,
                center_id: "",
                dates: [],
              });
              setDialogOpen(true);
              setIsEdit(false);
            }}
            disabled={dialogOpen}
            toolTip="Add new"
          >
            Add new
          </ButtonField>

          <IconButtonField
            className="refresh_btn"
            toolTip="Refresh"
            onClick={() => dispatch(ClassSelectionMasterRead())}
          >
            <SvgIcon
              className="refresh_svg_icon"
              component={RefreshButton}
              inheritViewBox
            />
          </IconButtonField>
        </CustomToolbar>
      </Table>

      {/* Form Dialog */}
      <Dialog
        open={dialogOpen}
        aria-labelledby="class-dialog-title"
        PaperProps={{
          sx: { maxWidth: "1000px" },
        }}
        fullWidth
      >
        <DialogTitle id="class-dialog-title">
          {isEdit ? "Update" : "Add"} Class Selection
        </DialogTitle>
        <DialogContent>
          <ClassSelectionForm
            formRef={formRef}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
            isEdit={isEdit}
          />
        </DialogContent>
        <DialogActions sx={{display:"flex", flexDirection:"row", justifyContent:"center"}}>
          <>
            <ButtonField
              className="form_cancel_button"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </ButtonField>
            <ButtonField
              className="form_save_button"
              onClick={() => {
                formRef.current?.requestSubmit();
              }}
            >
              Save
            </ButtonField>
          </>
        </DialogActions>
      </Dialog>

      <ResponseDialog
        okFunction={() => setDialogOpen(false)}
        response={createOrUpdateResponse}
        parentDialog={setDialogOpen}
      />

      {deleteDialogData && deleteDialogOpen && (
        <DeleteDialog
          deleteableName={""}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
          deleteFunction={() => {
            if (deleteDialogData?.class_selection_master_id) {
              const payload = {
                class_selection_master_id:
                  deleteDialogData.class_selection_master_id,
                tutor_id:
                  deleteDialogData.tutor_id || deleteDialogData.tutor?.id || 0,
                center_id:
                  deleteDialogData.center_id ||
                  deleteDialogData.center?.center_id ||
                  "",
                is_delete: true,
                status: deleteDialogData.status ?? true,
              };

              dispatch(ClassSelectionCreate_Update(payload));
            }
          }}
          response={createOrUpdateResponse}
        />
      )}
    </>
  );
};

export default ClassSelectionMaster;